import jsPDF from 'jspdf';
import { versRgb, dateCourte, dessinerEnTete, dessinerPied } from './planningPdfCommun';

/**
 * LE PDF DU CALENDRIER ANNUEL — 14 mois côte à côte, A4 paysage (lot 5.6).
 *
 * ── CE QUI CHANGE PAR RAPPORT À `ExportPDF.js`, QU'IL REMPLACE ───────────────
 *
 * 1. UN SEUL DOCUMENT POUR TOUTES LES COLONNES. L'ancienne modale produisait un
 *    fichier par colonne : six groupes, six PDF à ouvrir l'un après l'autre pour
 *    répondre à « et en BUT 2, c'est quelle semaine ? ». Chaque mois porte
 *    désormais autant de colonnes que demandé, comme la maquette le dessine.
 *
 * 2. LES ÉVÉNEMENTS PASSENT À LA LIGNE. L'ancien code s'arrêtait au premier qui
 *    dépassait la largeur (`if (evtX + labelWidth > pageW - marginX) return;`) :
 *    les suivants disparaissaient du document SANS que rien ne le dise. Ils sont
 *    répartis sur autant de lignes que nécessaire, et si la page n'y suffit
 *    plus, sur une SECONDE PAGE — jamais supprimés en silence.
 *
 * 3. AUCUNE CATÉGORIE N'EST ÉCRITE ICI. L'ancien fichier portait une constante
 *    `CATEGORIES` de huit entrées à emoji, doublon figé du référentiel : un
 *    département qui recolorait ses catégories voyait ses PDF garder les
 *    anciennes couleurs (écart E17). Ce module ne reçoit que des événements déjà
 *    résolus — libellé, date, couleur. Il ne sait pas ce qu'est une catégorie.
 *
 * ── LA CONTRAINTE QUI COMMANDE TOUTE LA MISE EN PAGE ─────────────────────────
 * Tout tient sur une page : 14 mois en largeur, jusqu'à 31 jours en hauteur. Les
 * dimensions ne sont donc pas choisies, elles se déduisent — et la zone du bas
 * (événements + légendes) est mesurée AVANT le calendrier, qui prend ce qui
 * reste. C'est l'inverse de l'ancien code, où le bas était posé à des ordonnées
 * fixes et ce qui n'y entrait pas était perdu.
 */

const PAGE_L = 297;          // A4 paysage, en millimètres
const PAGE_H = 210;
const MARGE_X = 5;
const ESPACE_MOIS = 0.9;
const JOUR_H_MAX = 5.2;
const JOUR_H_MIN = 3.4;      // en dessous, les numéros de jour ne se lisent plus
const LIGNE_EVT = 3.4;
const HAUT_TITRE_EVT = 4.2;

/* `versRgb`, `dateCourte`, les logos, l'en-tête et le pied vivent désormais dans
   `planningPdfCommun.js` : l'export Événements du lot 5.7 en a besoin à
   l'identique, et deux copies d'une identité visuelle finissent toujours par
   diverger. */

/**
 * Répartit les étiquettes d'événements en lignes qui tiennent dans `largeurMax`.
 * C'est le remplacement du `return` qui tronquait : rien ne se perd, la liste
 * s'allonge vers le bas.
 */
function repartirEnLignes(pdf, etiquettes, largeurMax) {
    const lignes = [];
    let courante = [];
    let largeur = 0;
    etiquettes.forEach(e => {
        const l = pdf.getTextWidth(e.texte) + 7;
        if (courante.length && largeur + l > largeurMax) {
            lignes.push(courante);
            courante = [];
            largeur = 0;
        }
        courante.push({ ...e, largeur: l });
        largeur += l;
    });
    if (courante.length) lignes.push(courante);
    return lignes;
}

function dessinerLigneEvenements(pdf, ligne, y) {
    let x = MARGE_X;
    ligne.forEach(e => {
        const rgb = versRgb(e.couleur);
        pdf.setFillColor(rgb.r, rgb.g, rgb.b);
        pdf.circle(x + 1.2, y - 1.1, 1.2, 'F');
        pdf.setTextColor(50, 50, 50);
        pdf.text(e.texte, x + 3.6, y);
        x += e.largeur;
    });
}

/**
 * @param {Object} p
 * @param {Array}  p.colonnes    colonnes RETENUES : {index, cle, label, couleur}
 * @param {Array}  p.mois        [{nom, annee, jours:[{date, numero_jour, jour_semaine_lettre, est_weekend, est_ferie}]}]
 * @param {Object} p.cellStates  clé « AAAA-MM-JJ-<index colonne> » -> code de type
 * @param {Array}  p.typesJournees [{code, libelle, couleur, couleur_texte}]
 * @param {Set}    p.typesEteints  « <clé colonne>|<code type> » présent = masqué
 * @param {Array}  p.evenements    [{id, date:'AAAA-MM-JJ', libelle, couleur}] triés par date
 * @param {Set}    p.evtEteints    « <clé colonne>|<id> » présent = masqué
 * @param {string} p.titre, p.sousTitre, p.auteur
 * @param {Object} p.logo           logo du DÉPARTEMENT — {data, width, height} ou null
 * @param {Object} p.logoUniversite logo de l'UNIVERSITÉ, posé à SA GAUCHE — idem
 * @returns {{pdf: jsPDF, pages: number, evenementsImprimes: number, jourH: number}}
 */
export function genererPdfCalendrier({
    colonnes = [],
    mois = [],
    cellStates = {},
    typesJournees = [],
    typesEteints = new Set(),
    evenements = [],
    evtEteints = new Set(),
    titre = '',
    sousTitre = '',
    auteur = '',
    logo = null,
    logoUniversite = null
}) {
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const largeurUtile = PAGE_L - MARGE_X * 2;

    // ── Ce qui est visible quelque part : un type masqué dans TOUTES les colonnes
    //    n'a pas à occuper la légende, et un événement masqué partout n'a pas à
    //    figurer dans la liste du bas.
    const visibleQuelquePart = (prefixe, eteints) =>
        colonnes.some(c => !eteints.has(c.cle + '|' + prefixe));
    const typesVisibles = typesJournees.filter(t => visibleQuelquePart(t.code, typesEteints));
    const evtVisibles = evenements.filter(e => visibleQuelquePart(e.id, evtEteints));

    /* ── UNE SEULE COLONNE : SON NOM DISPARAÎT ────────────────────────────────
       Demande de Bertrand à la 2ᵉ recette du lot 5.6. Un export « BUT 1 FI » ne
       contient qu'une colonne : répéter « BUT 1 FI » quatorze fois en haut des
       mois, plus une fois en légende, n'apprend rien — le TITRE du document le
       dit déjà, et il est en gros au-dessus. La bande de noms et la légende des
       colonnes ne servent qu'à DISTINGUER : sans rien à distinguer, elles ne
       sont pas simplifiables, elles sont inutiles.

       Le calendrier récupère la place ainsi libérée (près de 15 mm), donc des
       jours plus hauts et plus lisibles — c'est exactement le cas où l'on veut
       un planning bien lisible pour l'afficher. */
    const montrerNomsColonnes = colonnes.length > 1;

    // ── ZONE DU BAS, MESURÉE D'ABORD ─────────────────────────────────────────
    const yFooter = PAGE_H - 3;
    const yLegendeTypes = PAGE_H - 9;
    const yLegendeColonnes = montrerNomsColonnes ? PAGE_H - 14.5 : null;
    const basEvenements = (montrerNomsColonnes ? yLegendeColonnes : yLegendeTypes) - 2.5;

    pdf.setFontSize(7.5);
    pdf.setFont('helvetica', 'normal');
    const etiquettes = evtVisibles.map(e => ({
        texte: (e.libelle || '') + ' - ' + dateCourte(e.date),
        couleur: e.couleur
    }));
    const lignesEvt = repartirEnLignes(pdf, etiquettes, largeurUtile);

    // ── EN-TÊTE DE COLONNES : LE NOM ENTIER, ÉCRIT À LA VERTICALE ────────────
    // La maquette écrit « B1FI » à l'horizontale dans une bande de 3 mm. À six
    // colonnes, la bande fait 2 mm de large : le texte y était illisible, quand
    // il n'était pas purement supprimé faute de place. C'est un défaut du dessin,
    // relevé par Bertrand à la recette du lot 5.6 — une colonne qu'on ne sait pas
    // nommer ne sert à rien. Le nom COMPLET est donc écrit verticalement, dans
    // une bande dont la hauteur est calculée pour lui.
    const CAP_ENTETE = 24;
    pdf.setFontSize(5.5);
    const largeurNomMax = montrerNomsColonnes ? Math.max(...colonnes.map(c => pdf.getTextWidth(c.label))) : 0;
    const hauteurEnteteCol = montrerNomsColonnes ? Math.min(CAP_ENTETE, largeurNomMax + 3) : 0;

    const hautGrille = 18 + 5.5 + hauteurEnteteCol;  // titre + en-tête de mois + noms de colonnes
    const maxJours = Math.max(1, ...mois.map(m => m.jours.length));
    // Le calendrier garde la priorité : on ne lui prend que la place qui lui
    // reste au-dessus de sa hauteur minimale lisible.
    const placeEvt = basEvenements - (hautGrille + JOUR_H_MIN * maxJours);
    const lignesMax = lignesEvt.length
        ? Math.max(0, Math.floor((placeEvt - HAUT_TITRE_EVT) / LIGNE_EVT))
        : 0;
    const lignesPage1 = Math.min(lignesEvt.length, lignesMax);
    const lignesReportees = lignesEvt.slice(lignesPage1);
    const hauteurEvt = lignesPage1 ? HAUT_TITRE_EVT + lignesPage1 * LIGNE_EVT : 0;

    const finGrille = basEvenements - hauteurEvt;
    const jourH = Math.min(JOUR_H_MAX, (finGrille - hautGrille) / maxJours);
    // Les pages de report sont comptées, pas supposées : une seule page « suite »
    // suffit à 200 événements, mais pas à 600 — et un débordement muet est
    // exactement le défaut que ce lot corrige.
    const LIGNES_PAR_REPORT = Math.floor((PAGE_H - 22 - 8) / LIGNE_EVT);
    const pages = 1 + Math.ceil(lignesReportees.length / LIGNES_PAR_REPORT);

    // ── EN-TÊTE : LES DEUX LOGOS, PUIS LE TITRE CENTRÉ ───────────────────────
    // Fabrique partagée avec l'export Événements (lot 5.7). Les valeurs posées
    // ici sont celles que ce fichier portait en dur : la sortie est identique au
    // millimètre, vérifié en comparant les deux PDF.
    dessinerEnTete(pdf, {
        largeurPage: PAGE_L,
        margeX: MARGE_X,
        logo,
        logoUniversite,
        titre,
        titreParDefaut: 'Calendrier',
        sousTitre
    });

    // ── LA GRILLE ────────────────────────────────────────────────────────────
    const nbMois = Math.max(1, mois.length);
    const largeurMois = (largeurUtile - (nbMois - 1) * ESPACE_MOIS) / nbMois;
    const nbCol = Math.max(1, colonnes.length);
    // Lettre et numéro du jour prennent une part fixe, le reste se partage entre
    // les colonnes : à six colonnes elles font 2 mm, et c'est la limite du format.
    const largeurLettre = Math.min(largeurMois * 0.16, largeurMois / (2.5 + nbCol) * 1.25);
    const largeurNum = Math.min(largeurMois * 0.19, largeurMois / (2.5 + nbCol) * 1.25);
    const largeurCol = (largeurMois - largeurLettre - largeurNum) / nbCol;

    // Les événements, rangés par date puis par colonne où ils s'affichent.
    const evtParDate = {};
    evtVisibles.forEach(e => {
        if (!evtParDate[e.date]) evtParDate[e.date] = [];
        evtParDate[e.date].push(e);
    });
    const typeParCode = {};
    typesJournees.forEach(t => { typeParCode[t.code] = t; });

    const yEnteteMois = 18;
    const yEnteteCol = yEnteteMois + 5.5;
    const yPremierJour = yEnteteCol + hauteurEnteteCol;

    // Le nom vertical doit tenir dans la LARGEUR de sa colonne (2 mm à six
    // colonnes, 1,2 mm à douze) : la taille se déduit donc des deux contraintes,
    // la hauteur de bande disponible et la largeur de la colonne.
    const tailleNom = Math.min(
        5.5,
        largeurNomMax > 0 ? 5.5 * (CAP_ENTETE - 3) / largeurNomMax : 5.5,
        largeurCol / 0.3528 * 0.85
    );

    mois.forEach((m, iMois) => {
        const x = MARGE_X + iMois * (largeurMois + ESPACE_MOIS);

        pdf.setFillColor(30, 64, 175);
        pdf.rect(x, yEnteteMois, largeurMois, 5.5, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'bold');
        pdf.text(m.nom.substring(0, 4) + '. ' + String(m.annee).slice(2), x + largeurMois / 2, yEnteteMois + 3.9, { align: 'center' });

        // En-tête de colonnes : sans elle, six bandes de couleur ne se
        // distinguent que par la légende du bas — trop loin de la grille.
        if (montrerNomsColonnes) colonnes.forEach((c, iCol) => {
            const cx = x + largeurLettre + largeurNum + iCol * largeurCol;
            const rgb = versRgb(c.couleur);
            pdf.setFillColor(rgb.r, rgb.g, rgb.b);
            pdf.rect(cx, yEnteteCol, largeurCol, hauteurEnteteCol, 'F');
            pdf.setFontSize(tailleNom);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(255, 255, 255);
            // `angle: 90` fait courir la ligne de base vers le HAUT : le nom se
            // lit de bas en haut, comme au dos d'un livre. Le décalage recentre
            // la ligne de base dans la largeur de la colonne.
            pdf.text(c.label, cx + largeurCol / 2 + tailleNom * 0.3528 * 0.35, yEnteteCol + hauteurEnteteCol - 1.5, { angle: 90 });
        });

        m.jours.forEach((jour, iJour) => {
            const y = yPremierJour + iJour * jourH;
            const dateStr = typeof jour.date === 'string'
                ? jour.date.split('T')[0]
                : new Date(jour.date).toISOString().split('T')[0];

            // Colonne des repères (lettre + numéro), commune aux colonnes du mois
            if (jour.est_weekend || jour.est_ferie) {
                pdf.setFillColor(jour.est_ferie ? 254 : 235, jour.est_ferie ? 226 : 238, jour.est_ferie ? 226 : 245);
                pdf.rect(x, y, largeurMois, jourH, 'F');
            }
            pdf.setFontSize(Math.min(5.4, jourH * 1.15));
            pdf.setFont('helvetica', 'bold');
            if (jour.est_ferie) pdf.setTextColor(153, 27, 27);
            else if (jour.est_weekend) pdf.setTextColor(150, 150, 150);
            else pdf.setTextColor(100, 116, 139);
            pdf.text(jour.jour_semaine_lettre, x + largeurLettre / 2, y + jourH / 2 + 0.9, { align: 'center' });
            pdf.setFont('helvetica', 'normal');
            if (!jour.est_ferie) pdf.setTextColor(30, 41, 59);
            // Le numéro se décale à gauche : la pastille occupe le bord droit.
            pdf.text(String(jour.numero_jour), x + largeurLettre + largeurNum / 2 - 0.55, y + jourH / 2 + 0.9, { align: 'center' });

            // ── LES DATES IMPORTANTES SONT SUR LE NUMÉRO DE JOUR ──────────────
            // Comme dans le calendrier à l'écran (`.day-number` porte
            // `.event-pastilles`), et non dans les colonnes de formation où la
            // première version les avait mises : un événement du département ne
            // concerne pas une colonne, il concerne une DATE. Défaut relevé par
            // Bertrand à la recette du lot 5.6.
            //
            // La matrice « événements × colonnes » garde son rôle : elle décide
            // si l'événement fait partie de l'export. Il s'affiche dès qu'il est
            // retenu sur AU MOINS UNE colonne — la même règle que la liste du bas.
            const duJour = (evtParDate[dateStr] || [])
                .filter(e => colonnes.some(c => !evtEteints.has(c.cle + '|' + e.id)));
            if (duJour.length) {
                // Empilées le long du bord droit de la case, comme la rangée de
                // pastilles de l'écran. Au-delà de trois, la place manque : la
                // liste « Événements importants » du bas, elle, est exhaustive.
                const visibles = duJour.slice(0, 3);
                // Le rayon se déduit de la place réelle — la pile doit tenir dans
                // la hauteur du jour, sinon deux pastilles se chevauchent.
                const r = Math.min(0.62, largeurNum * 0.28, (jourH - 0.5) / (2 * visibles.length));
                const bord = x + largeurLettre + largeurNum - r - 0.15;
                const hauteurPile = visibles.length * (r * 2 + 0.25) - 0.25;
                visibles.forEach((e, iP) => {
                    const rgb = versRgb(e.couleur);
                    pdf.setFillColor(rgb.r, rgb.g, rgb.b);
                    pdf.circle(bord, y + (jourH - hauteurPile) / 2 + r + iP * (r * 2 + 0.25), r, 'F');
                });
            }

            colonnes.forEach((c, iCol) => {
                const cx = x + largeurLettre + largeurNum + iCol * largeurCol;
                const code = cellStates[dateStr + '-' + c.index] || '';
                const type = code ? typeParCode[code] : null;
                const actif = !!type
                    && !typesEteints.has(c.cle + '|' + code)
                    && !jour.est_weekend && !jour.est_ferie;

                if (actif) {
                    const rgb = versRgb(type.couleur);
                    pdf.setFillColor(rgb.r, rgb.g, rgb.b);
                    pdf.rect(cx, y, largeurCol, jourH, 'F');
                    if (largeurCol >= 3.4) {
                        const t = versRgb(type.couleur_texte || '#FFFFFF');
                        pdf.setTextColor(t.r, t.g, t.b);
                        pdf.setFont('helvetica', 'bold');
                        pdf.setFontSize(Math.min(5.4, largeurCol * 1.5));
                        pdf.text(code, cx + largeurCol / 2, y + jourH / 2 + 0.9, { align: 'center' });
                        pdf.setFont('helvetica', 'normal');
                    }
                }

            });

            pdf.setDrawColor(226, 232, 240);
            pdf.setLineWidth(0.08);
            pdf.rect(x, y, largeurMois, jourH, 'S');
        });
    });

    // ── ÉVÉNEMENTS IMPORTANTS ────────────────────────────────────────────────
    if (lignesPage1) {
        const yTitre = finGrille + HAUT_TITRE_EVT - 1;
        pdf.setFontSize(8.5);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 41, 59);
        pdf.text('Événements importants', PAGE_L / 2, yTitre, { align: 'center' });
        pdf.setFontSize(7.5);
        pdf.setFont('helvetica', 'normal');
        lignesEvt.slice(0, lignesPage1).forEach((ligne, i) => {
            dessinerLigneEvenements(pdf, ligne, yTitre + 3.4 + i * LIGNE_EVT);
        });
    }

    // ── LÉGENDES : les colonnes d'abord, les types ensuite ───────────────────
    const dessinerLegende = (entrees, y) => {
        let x = MARGE_X;
        pdf.setFontSize(7);
        entrees.forEach(e => {
            const rgb = versRgb(e.couleur);
            pdf.setFillColor(rgb.r, rgb.g, rgb.b);
            pdf.rect(x, y, 8, 3.6, 'F');
            if (e.code) {
                const t = versRgb(e.couleurTexte || '#FFFFFF');
                pdf.setTextColor(t.r, t.g, t.b);
                pdf.setFont('helvetica', 'bold');
                pdf.text(e.code, x + 4, y + 2.6, { align: 'center' });
            }
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(30, 41, 59);
            pdf.text(e.libelle, x + 9.5, y + 2.6);
            x += 9.5 + pdf.getTextWidth(e.libelle) + 6;
        });
    };
    if (montrerNomsColonnes) {
        dessinerLegende(colonnes.map(c => ({ couleur: c.couleur, libelle: c.label })), yLegendeColonnes);
    }
    dessinerLegende(typesVisibles.map(t => ({
        couleur: t.couleur, couleurTexte: t.couleur_texte, code: t.code, libelle: t.libelle
    })), yLegendeTypes);

    // ── PIED DE PAGE ─────────────────────────────────────────────────────────
    const pied = (n) => dessinerPied(pdf, {
        largeurPage: PAGE_L, margeX: MARGE_X, y: yFooter, page: n, pages, auteur
    });
    pied(1);

    // ── PAGES DE REPORT : le reste des événements ────────────────────────────
    // Elles n'existent que si la première n'a pas pu tout porter. Mieux vaut une
    // page de plus qu'une liste amputée sans le dire.
    for (let p = 0; p * LIGNES_PAR_REPORT < lignesReportees.length; p++) {
        pdf.addPage();
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(15, 23, 42);
        pdf.text('Événements importants (suite)', PAGE_L / 2, 14, { align: 'center' });
        pdf.setFontSize(7.5);
        pdf.setFont('helvetica', 'normal');
        lignesReportees
            .slice(p * LIGNES_PAR_REPORT, (p + 1) * LIGNES_PAR_REPORT)
            .forEach((ligne, i) => dessinerLigneEvenements(pdf, ligne, 22 + i * LIGNE_EVT));
        pied(p + 2);
    }

    return { pdf, pages, evenementsImprimes: evtVisibles.length, jourH };
}
